/**
 * Envio y devolucion de la oferta de un producto (SEO-03).
 *
 * Search Console los pide en "Fichas de comerciantes" (`shippingDetails` y
 * `hasMerchantReturnPolicy`). Salen de las MISMAS opciones de despacho que usa
 * el checkout, nunca escritos a mano: si el panel apaga el despacho, el
 * esquema deja de anunciarlo en la misma carga.
 */
import type { OpcionesDespacho, RegionDespacho } from '@bandu/compartido';

import { ofreceDespacho } from '../utilidades/entrega';
import type { Esquema } from './esquemas';

const PAIS = 'CL';
const MONEDA = 'CLP';

/**
 * SUPUESTO: el negocio no ha definido una politica de devoluciones. Se declara
 * el minimo de la ley del consumidor para compras a distancia (derecho de
 * retracto, 10 dias, art. 3 bis de la Ley 19.496). Confirmar con el cliente
 * (Pendientes-cliente.md) y ajustar aca.
 */
const DIAS_DEVOLUCION = 10;

/** "3 a 4 dias habiles" o "24 a 48 horas" → dias habiles minimo y maximo. */
function plazoEnDias(plazo: string): { minimo: number; maximo: number } | null {
  const calce = /(\d+)\s*a\s*(\d+)\s*(dias|horas)/i.exec(plazo);
  if (calce === null) return null;
  const divisor = calce[3]?.toLowerCase() === 'horas' ? 24 : 1;
  return {
    minimo: Math.max(0, Math.floor(Number(calce[1]) / divisor)),
    maximo: Math.max(1, Math.ceil(Number(calce[2]) / divisor)),
  };
}

/**
 * Una region con su costo base. Las comunas con precio propio quedan fuera: el
 * esquema publica la tarifa de la region, que es la que paga la mayoria.
 */
function envioARegion(region: RegionDespacho, gratis: boolean): Esquema {
  const dias = plazoEnDias(region.plazo);
  return {
    '@type': 'OfferShippingDetails',
    shippingDestination: {
      '@type': 'DefinedRegion',
      addressCountry: PAIS,
      addressRegion: region.codigo,
    },
    shippingRate: {
      '@type': 'MonetaryAmount',
      value: gratis ? 0 : region.costo,
      currency: MONEDA,
    },
    ...(dias !== null
      ? {
          deliveryTime: {
            '@type': 'ShippingDeliveryTime',
            handlingTime: { '@type': 'QuantitativeValue', minValue: 0, maxValue: 1, unitCode: 'DAY' },
            transitTime: {
              '@type': 'QuantitativeValue',
              minValue: dias.minimo,
              maxValue: dias.maximo,
              unitCode: 'DAY',
            },
          },
        }
      : {}),
  };
}

/**
 * Con despacho, una tarifa por region (gratis si el precio ya supera el umbral).
 * Sin despacho —solo retiro o entrega por coordinar— se declara que no se
 * envia: prometer una tarifa que el checkout no ofrece es peor que no tenerla.
 * Mientras las opciones no llegan, nada.
 */
export function esquemaEnvio(opciones: OpcionesDespacho | null, precio: number): Esquema[] {
  if (opciones === null) return [];
  if (!ofreceDespacho(opciones)) {
    return [
      {
        '@type': 'OfferShippingDetails',
        shippingDestination: { '@type': 'DefinedRegion', addressCountry: PAIS },
        doesNotShip: true,
      },
    ];
  }
  const gratis = precio >= opciones.despachoGratisDesde;
  return opciones.regiones.map((region) => envioARegion(region, gratis));
}

export function esquemaDevolucion(): Esquema {
  return {
    '@type': 'MerchantReturnPolicy',
    applicableCountry: PAIS,
    returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
    merchantReturnDays: DIAS_DEVOLUCION,
  };
}
